import { useAuth } from '@contexts/authContext';
import { Application } from '@core/Application';
import { Organization } from '@models/Organization';
import { PilotOnboardingService } from '@services/PilotOnboardingService';
import { UsersService } from '@services/UsersService';
import { HollowedButton, IconButton, RefreshButton } from '@shared/designSystem/Button';
import { HorizontalContainer, TableCell, TableHeaderCell, TableHeaderContainer, TableRowContainer, VerticalContainer } from '@shared/designSystem/Containers';
import FilterPillGroup from '@shared/designSystem/FilterPillGroup';
import { StatusPill } from '@shared/designSystem/StatusPill';
import SearchBar from '@shared/designSystem/SearchBar';
import { QontyEmptyState } from '@components/QontyEmptyState';
import { QontyLoading } from '@components/QontyLoading';
import ClickableWrapper from '@shared/designSystem/ClickableWrapper';
import designSystemStyles from '@shared/styles/designSystemStyles';
import EmptyContent from '@components/General/EmptyContent';
import { canAccessPilotOnboarding } from './PilotOnboardingAccess';
import { useMemo } from 'react';
import PilotOnboardingStartModalScreen from './PilotOnboardingStartModalScreen';
import { PilotStatus, PilotStatusFilter, usePilotOnboardingTableViewModel } from './PilotOnboardingTableViewModel';
import { H3Title } from '@shared/designSystem/Titles';
import PilotOnboardingCloseModalScreen from './PilotOnboardingCloseModalScreen';
import PilotOnboardingConvertModalScreen from './PilotOnboardingConvertModalScreen';
import { ToastManager } from '@shared/designSystem/messages/ToastManager';
import { SmallText, Text } from '@shared/designSystem/Texts';
import PilotOnboardingDetailModal from './PilotOnboardingDetailModal';

const pilotStatusDotColors: Record<PilotStatus, string> = {
    active: designSystemStyles.colorStatusSuccess,
    lowUsage: designSystemStyles.colorWarning,
    noUsage: designSystemStyles.colorStatusNeutral,
};

interface PilotOnboardingScreenProps {
    org: Organization;
}

const PilotOnboardingScreen: React.FC<PilotOnboardingScreenProps> = ({ org }) => {
    const { isSystemAdmin, user, validateWithQontact } = useAuth();
    const pilotOnboardingService = useMemo(() => {
        return new PilotOnboardingService(Application.shared().coreDependencies.httpClient);
    }, []);
    const usersService = useMemo(() => {
        return new UsersService(Application.shared().coreDependencies.httpClient);
    }, []);
    const accessGranted = canAccessPilotOnboarding({
        env: process.env.REACT_APP_FRONT_ENV,
        organizationID: org?.id,
        isSystemAdmin,
    });
    const viewModel = usePilotOnboardingTableViewModel({
        pilotOnboardingService,
    });
    const columnWidths = {
        id: "17%",
        name: "23%",
        country: "6%",
        creationDate: "9%",
        lastTimeUpdated: "9%",
        totalUsers: "8%",
        usersLastWeek: "8%",
        status: "12%",
        actions: "8%",
    };

    const statusFilterOptions = [
        { id: 'all', label: 'Todos'.localized() },
        { id: 'withActivity', label: 'Con actividad'.localized() },
        { id: 'noUsage', label: 'Sin uso'.localized() },
    ];

    const refreshPilotData = async () => {
        await viewModel.refresh();

        if (!user) {
            return;
        }

        const token = await user.getIdToken();
        await validateWithQontact(token, user.uid, { forceRefresh: true });
    };

    const handleClosePilot = async () => {
        const result = await viewModel.closePilot(user?.uid);

        if (result.kind === 'success') {
            ToastManager.shared().showSuccessMessage(result.message);
            await refreshPilotData();
            return;
        }

        ToastManager.shared().showErrorMessage(result.message);
    };

    const handleConvertPilot = async () => {
        const result = await viewModel.convertPilot(user?.uid);

        if (result.kind === 'success') {
            ToastManager.shared().showSuccessMessage(result.message);
            await refreshPilotData();
            return;
        }

        ToastManager.shared().showErrorMessage(result.message);
    };

    if (!accessGranted) {
        return <EmptyContent />;
    }

    return (
        <VerticalContainer 
            paddingLeft={designSystemStyles.paddingLg}
            paddingRight={designSystemStyles.paddingLg}
            height="100%"
            gap={designSystemStyles.gapLg}
        >
            <VerticalContainer gap={designSystemStyles.paddingZero}>
                <H3Title text={"Pilotos en curso".localized()} />
                <SmallText
                    text={`${viewModel.totalPilotsCount} ${"organizaciones en piloto".localized()} · ${viewModel.activeThisWeekCount} ${"con actividad esta semana".localized()}`}
                    color={designSystemStyles.colorTextSecondary}
                />
            </VerticalContainer>
            <HorizontalContainer height={designSystemStyles.sizeRowMedium} gap={designSystemStyles.gapMd}>
                <SearchBar
                    placeholder={"Buscar por ID, nombre, razon social, customer o pais".localized()}
                    value={viewModel.searchField}
                    onChange={viewModel.setSearchField}
                />
                <FilterPillGroup
                    options={statusFilterOptions}
                    selectedId={viewModel.statusFilter}
                    onSelect={(id) => viewModel.setStatusFilter(id as PilotStatusFilter)}
                    ariaLabel={"Filtrar pilotos por uso".localized()}
                    wrap={false}
                />
                <RefreshButton text="" onClick={() => void viewModel.refresh()} />
                <HollowedButton
                    text={"Iniciar piloto".localized()}
                    onClick={viewModel.openStartModal}
                    width="220px"
                />
            </HorizontalContainer>

            {viewModel.loading ? (
                <QontyLoading
                    text={"Cargando pilotos...".localized()}
                    withQonty={true}
                    darkMode={false}
                />
            ) : viewModel.loadErrorMessage && viewModel.allRows.length === 0 ? (
                <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center" gap={designSystemStyles.gapMd}>
                    <QontyEmptyState
                        text={viewModel.loadErrorMessage.localized()}
                        darkMode={false}
                    />
                </VerticalContainer>
            ) : viewModel.rows.length === 0 ? (
                <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center">
                    <QontyEmptyState
                        text={"No hay pilotos para mostrar.".localized()}
                        darkMode={false}
                    />
                </VerticalContainer>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, gap: designSystemStyles.gapMd, width: "100%" }}>
                    {viewModel.loadErrorMessage && (
                        <VerticalContainer width="100%" padding={designSystemStyles.paddingSm}>
                            <QontyEmptyState
                                text={viewModel.loadErrorMessage}
                                darkMode={false}
                                fullSize={false}
                            />
                        </VerticalContainer>
                    )}
                    <TableHeaderContainer height={designSystemStyles.sizeRowMedium}>
                        <TableHeaderCell text={"ID".localized()} width={columnWidths.id} />
                        <TableHeaderCell text={"Nombre".localized()} width={columnWidths.name} />
                        <TableHeaderCell text={"Pais".localized()} width={columnWidths.country} />
                        <TableHeaderCell text={"Creacion".localized()} width={columnWidths.creationDate} />
                        <TableHeaderCell text={"Ult. actividad".localized()} width={columnWidths.lastTimeUpdated} />
                        <TableHeaderCell text={"Usuarios".localized()} width={columnWidths.totalUsers} justifyContent="right" />
                        <TableHeaderCell text={"Usuarios sem.".localized()} width={columnWidths.usersLastWeek} justifyContent="right" />
                        <TableHeaderCell text={"Estado".localized()} width={columnWidths.status} />
                        <TableHeaderCell text={"Acciones".localized()} width={columnWidths.actions} justifyContent="flex-end" />
                    </TableHeaderContainer>
                    <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapNone, overflowY: "auto", flex: 1, minHeight: 0, width: "100%" }}>
                        {viewModel.rows.map((row) => (
                            <ClickableWrapper
                                key={row.rowKey}
                                onClick={() => viewModel.openDetailModal(row)}
                                style={{ width: "100%" }}
                                title={"Ver detalle del piloto".localized()}
                            >
                                <TableRowContainer>
                                    <HorizontalContainer width={columnWidths.id} minWidth="0">
                                        <Text
                                            text={"**"+row.id+"**"}
                                            truncate={true}
                                            markdown={true}
                                            color={row.isIDHighlighted ? designSystemStyles.colorError : undefined}
                                        />
                                    </HorizontalContainer>
                                    <TableCell text={row.name} width={columnWidths.name} truncate={true} />
                                    <TableCell text={row.countryCode} width={columnWidths.country} truncate={true} />
                                    <TableCell text={row.creationDate} width={columnWidths.creationDate} truncate={true} small={true} />
                                    <TableCell text={row.lastTimeUpdated} width={columnWidths.lastTimeUpdated} truncate={true} small={true} />
                                    <TableCell text={row.totalUsers} width={columnWidths.totalUsers} justifyContent="right" truncate={true} />
                                    <TableCell text={row.usersLastWeek} width={columnWidths.usersLastWeek} justifyContent="right" truncate={true} />
                                    <HorizontalContainer width={columnWidths.status} minWidth="0">
                                        <StatusPill
                                            label={row.statusLabel}
                                            dotColor={pilotStatusDotColors[row.status]}
                                        />
                                    </HorizontalContainer>
                                    <div
                                        onClick={(event) => event.stopPropagation()}
                                        onKeyDown={(event) => event.stopPropagation()}
                                        style={{ width: columnWidths.actions, minWidth: 0 }}
                                    >
                                        <HorizontalContainer width="100%" justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                                            <IconButton
                                                iconType="send"
                                                iconColor={designSystemStyles.colorSuccess}
                                                onClick={() => viewModel.openConvertModal(row)}
                                                tooltip={row.organizationID ? "Convertir a cliente".localized() : "Piloto sin ID de organizacion".localized()}
                                                disabled={!row.organizationID}
                                            />
                                            <IconButton
                                                iconType="delete"
                                                iconColor={designSystemStyles.colorError}
                                                onClick={() => viewModel.openCloseModal(row)}
                                                tooltip={row.organizationID ? "Cerrar piloto".localized() : "Piloto sin ID de organizacion".localized()}
                                                disabled={!row.organizationID}
                                            />
                                        </HorizontalContainer>
                                    </div>
                                </TableRowContainer>
                            </ClickableWrapper>
                        ))}
                    </div>
                </div>
            )}

            <PilotOnboardingStartModalScreen
                isOpen={viewModel.isStartModalOpen}
                onClose={viewModel.closeStartModal}
                onSuccess={refreshPilotData}
                org={org}
                pilotOnboardingService={pilotOnboardingService}
                usersService={usersService}
            />
            <PilotOnboardingDetailModal
                isOpen={viewModel.isDetailModalOpen}
                pilot={viewModel.selectedPilotDetail}
                pilotOnboardingService={pilotOnboardingService}
                onClose={viewModel.closeDetailModal}
                onSaved={refreshPilotData}
            />
            <PilotOnboardingConvertModalScreen
                isOpen={viewModel.isConvertModalOpen}
                pilot={viewModel.pilotPendingConvert}
                isConverting={viewModel.isConvertingPilot}
                onClose={viewModel.closeConvertModal}
                onConfirm={() => void handleConvertPilot()}
            />
            <PilotOnboardingCloseModalScreen
                isOpen={viewModel.isCloseModalOpen}
                pilot={viewModel.pilotPendingClose}
                isClosing={viewModel.isClosingPilot}
                onClose={viewModel.closeCloseModal}
                onConfirm={() => void handleClosePilot()}
            />
        </VerticalContainer>
    );
};

export default PilotOnboardingScreen;
